import { FC, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form-latest';
import { ModalContainer, ModalHeader, ModalActions } from '@/utils/modal';
import { useUserTableViews } from '@/utils/userTableSettings/UserTableSettingsContext';
import { errorToast } from '@/helpers';
import styles from './SaveViewModal.module.scss';
import type { UserTableViewSettings, UserTableViewTableName } from '@/model/UserTableView.model.ts';

export interface SaveViewModalProps {
  tableName: UserTableViewTableName;
  settings: UserTableViewSettings;
  onViewSaved: (settings: UserTableViewSettings) => void;
  closeModal: () => void;
}

interface SaveViewFormData {
  viewName: string;
}

const RESERVED_NAMES = new Set(['default']);

const SaveViewModal: FC<SaveViewModalProps> = ({ tableName, settings, onViewSaved, closeModal }) => {
  const { createView, getViews } = useUserTableViews();
  const [isSaving, setIsSaving] = useState(false);

  const views = getViews(tableName);
  const existingNames = useMemo(() => new Set(views.map((view) => view.name.toLowerCase())), [views]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SaveViewFormData>({
    mode: 'onChange',
    defaultValues: { viewName: '' },
  });

  const validateViewName = (value: string): string | true => {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === '') {
      return 'Please enter a name for this view';
    }
    if (RESERVED_NAMES.has(trimmed)) {
      return 'This name is reserved';
    }
    if (existingNames.has(trimmed)) {
      return 'A view with this name already exists';
    }
    return true;
  };

  const onSubmit = async (data: SaveViewFormData) => {
    setIsSaving(true);

    const newView = await createView(tableName, data.viewName.trim(), settings, true);

    if (newView !== null) {
      onViewSaved(newView.settings);
      closeModal();
    } else {
      errorToast('Failed to save view. Please try again.');
      setIsSaving(false);
    }
  };

  const fieldError = errors.viewName?.message;

  return (
    <ModalContainer size="narrow" data-test-component="SaveViewModal">
      <ModalHeader title="Save view" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="pa3">
          <p className={styles.hint} data-test-component="SaveViewModal" data-test-element="hint">
            This will save your current columns, sort, page size, and filters.
          </p>

          <div className={styles.inputGroup}>
            <label htmlFor="view-name" className={styles.label}>
              View name
            </label>
            <input
              id="view-name"
              type="text"
              className={fieldError ? 'input__error' : ''}
              placeholder="Enter a name for this view..."
              maxLength={100}
              autoFocus
              data-test-component="SaveViewModal"
              data-test-element="name-input"
              {...register('viewName', { validate: validateViewName })}
            />
            {fieldError && (
              <small className="red db" data-test-component="SaveViewModal" data-test-element="error">
                {fieldError}
              </small>
            )}
          </div>
        </div>

        <ModalActions
          submitBtnText="Save view"
          submitButtonProps={{
            disabled: isSaving || !isValid,
            loading: isSaving,
          }}
          onSubmit={handleSubmit(onSubmit)}
          onCancel={closeModal}
          className="pa3 bt b--moon-gray"
        />
      </form>
    </ModalContainer>
  );
};

export default SaveViewModal;
